import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Eye,
  EyeOff,
  Shield,
  Lock,
  User,
  CheckCircle,
  XCircle,
  Clock,
  Building,
  UserPlus,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { authApi, handleApiError } from '@/services/api';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Card from '@/components/ui/Card';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import toast from 'react-hot-toast';

const acceptSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number'),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ['confirmPassword'],
});

type AcceptForm = z.infer<typeof acceptSchema>;

interface InvitationInfo {
  organization_name: string;
  invited_by_name: string | null;
  role: string;
  email: string;
  expires_at: string;
  /** The invited email already has a Zamino account → accept by logging in,
   *  not by setting a new password (Workspaces Layer 3, confidential invites). */
  accountExists: boolean;
}

const ROLE_LABELS: Record<string, string> = {
  'workspace-admin': 'Admin',
  'workspace-member': 'Member',
};
const roleLabel = (role: string): string => ROLE_LABELS[role] ?? role;

type PageState =
  | { kind: 'loading' }
  | { kind: 'valid'; info: InvitationInfo }
  | { kind: 'error'; message: string; type: 'expired' | 'revoked' | 'invalid' | 'accepted' };

const InviteAcceptPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, refreshUser, switchWorkspace } = useAuth();
  const [pageState, setPageState] = useState<PageState>({ kind: 'loading' });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<AcceptForm>({
    resolver: zodResolver(acceptSchema),
    mode: 'onChange',
  });

  const passwordValue = watch('password');

  useEffect(() => {
    if (!token) {
      setPageState({ kind: 'error', message: 'Invalid invitation link.', type: 'invalid' });
      return;
    }

    authApi.validateInvitation(token)
      .then((data) => {
        if (data.valid) {
          setPageState({
            kind: 'valid',
            info: {
              organization_name: data.organization_name!,
              invited_by_name: data.invited_by_name ?? null,
              role: data.role!,
              email: data.email!,
              expires_at: data.expires_at!,
              accountExists: !!data.accountExists,
            },
          });
        } else {
          const msg = data.message || 'This invitation is no longer valid.';
          let type: 'expired' | 'revoked' | 'invalid' | 'accepted' = 'invalid';
          if (msg.includes('expired')) type = 'expired';
          else if (msg.includes('revoked')) type = 'revoked';
          else if (msg.includes('accepted')) type = 'accepted';
          setPageState({ kind: 'error', message: msg, type });
        }
      })
      .catch((err) => {
        const msg = err.response?.data?.message || 'This invitation is no longer valid.';
        let type: 'expired' | 'revoked' | 'invalid' | 'accepted' = 'invalid';
        if (msg.includes('expired')) type = 'expired';
        else if (msg.includes('revoked')) type = 'revoked';
        else if (msg.includes('accepted')) type = 'accepted';
        setPageState({ kind: 'error', message: msg, type });
      });
  }, [token]);

  const onSubmit = async (data: AcceptForm) => {
    if (!token) return;
    try {
      setIsSubmitting(true);
      const result = await authApi.acceptInvitation(token, {
        firstName: data.firstName,
        lastName: data.lastName,
        password: data.password,
      });
      // Race: the email became a real account between validate and submit. The
      // backend won't create a duplicate — it asks the invitee to log in. Flip
      // the page into the existing-account branch instead of erroring.
      if (result.requiresLogin) {
        setPageState((s) =>
          s.kind === 'valid' ? { kind: 'valid', info: { ...s.info, accountExists: true } } : s,
        );
        toast('You already have an account — log in to accept.', { icon: 'ℹ️' });
        return;
      }
      // New account created + token stored by authApi.acceptInvitation — hydrate
      // and enter. 2FA is optional (enable later from Settings → Security).
      const orgName = pageState.kind === 'valid' ? pageState.info.organization_name : 'the team';
      toast.success(`Welcome to ${orgName}!`);
      await refreshUser();
      navigate('/home');
    } catch (err) {
      toast.error(handleApiError(err) || 'Failed to accept invitation. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Existing-account accept. The invitee is logged in as the invited email;
   * attach the membership + grants server-side, switch into the invited
   * business's WORKSPACE, and enter. A different session email 403s.
   */
  const handleAuthenticatedAccept = async () => {
    if (!token) return;
    try {
      setIsSubmitting(true);
      const { organization, workspaceId } = await authApi.acceptInvitationAuthenticated(token);
      if (workspaceId) await switchWorkspace(workspaceId);
      toast.success(`You've joined ${organization.name}!`);
      navigate('/home');
    } catch (err) {
      toast.error(handleApiError(err) || 'Could not accept the invitation.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPasswordStrength = (password: string) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    return strength;
  };

  if (pageState.kind === 'loading') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center space-y-4">
          <LoadingSpinner size="lg" />
          <p className="text-muted-foreground">Validating your invitation...</p>
        </div>
      </div>
    );
  }

  if (pageState.kind === 'error') {
    const iconMap = {
      expired: <Clock className="w-8 h-8 text-warn" />,
      revoked: <XCircle className="w-8 h-8 text-destructive" />,
      invalid: <XCircle className="w-8 h-8 text-destructive" />,
      accepted: <CheckCircle className="w-8 h-8 text-success" />,
    };

    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-md w-full space-y-6">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-card rounded-full flex items-center justify-center shadow-lg">
              {iconMap[pageState.type]}
            </div>
            <h1 className="mt-6 text-2xl font-bold text-foreground">
              {pageState.type === 'accepted' ? 'Already Accepted' : 'Invitation Unavailable'}
            </h1>
          </div>

          <Card>
            <div className="text-center space-y-4">
              <p className="text-muted-foreground">{pageState.message}</p>

              {pageState.type === 'expired' && (
                <p className="text-sm text-muted-foreground">
                  Ask your organization admin to send a new invitation.
                </p>
              )}

              {pageState.type === 'accepted' && isAuthenticated ? (
                <Button onClick={() => navigate('/home')} className="w-full" size="lg">
                  Go to Zamino
                </Button>
              ) : (
                <Link
                  to="/login"
                  className="inline-block text-primary hover:text-primary/80 font-medium text-sm"
                >
                  Go to login
                </Link>
              )}
            </div>
          </Card>
        </div>
      </div>
    );
  }

  const { info } = pageState;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto w-16 h-16 bg-primary rounded-full flex items-center justify-center shadow-lg">
            <UserPlus className="w-8 h-8 text-primary-foreground" />
          </div>
          <h1 className="mt-6 text-3xl font-bold text-foreground">
            Join {info.organization_name}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {info.invited_by_name
              ? `${info.invited_by_name} invited you to join as ${roleLabel(info.role)}`
              : `You've been invited to join as ${roleLabel(info.role)}`}
          </p>
        </div>

        {/* Org Info Banner */}
        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground bg-white/60 rounded-lg py-3 px-4">
          <Building className="w-4 h-4 text-primary flex-shrink-0" />
          <span>
            You'll join <strong className="text-foreground">{info.organization_name}</strong> as{' '}
            <strong className="text-foreground">{roleLabel(info.role)}</strong>
          </span>
        </div>

        {/* Existing-account branch: accept by logging in, not by creating one. */}
        {info.accountExists ? (
          <Card className="mt-8">
            <div className="space-y-6 text-center">
              <div>
                <h2 className="text-lg font-semibold text-foreground">You already have a Zamino account</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  {isAuthenticated
                    ? `Accept to add ${info.organization_name} to your account.`
                    : `Sign in as ${info.email} to accept this invitation.`}
                </p>
              </div>
              {isAuthenticated ? (
                <Button className="w-full" size="lg" isLoading={isSubmitting} onClick={handleAuthenticatedAccept}>
                  Accept Invitation
                </Button>
              ) : (
                <Button className="w-full" size="lg" onClick={() => navigate('/login')}>
                  Log in to accept
                </Button>
              )}
              <p className="text-xs text-muted-foreground">
                After signing in, re-open this invitation link to finish accepting.
              </p>
            </div>
          </Card>
        ) : (
        /* Registration Form (new account) */
        <Card className="mt-8">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="text-center">
              <h2 className="text-lg font-semibold text-foreground">
                Create Your Account
              </h2>
              <p className="text-sm text-muted-foreground">
                Set up your account to get started
              </p>
            </div>

            {/* Email (read-only) */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Email Address
              </label>
              <div className="px-3 py-2 bg-muted border border-input rounded-lg text-muted-foreground text-sm cursor-not-allowed flex items-center gap-2">
                <Shield className="w-4 h-4 text-muted-foreground" />
                {info.email}
              </div>
            </div>

            {/* Name fields */}
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="First Name"
                type="text"
                autoComplete="given-name"
                leftIcon={<User className="w-4 h-4 text-muted-foreground" />}
                placeholder="John"
                error={errors.firstName?.message}
                {...register('firstName')}
              />
              <Input
                label="Last Name"
                type="text"
                autoComplete="family-name"
                leftIcon={<User className="w-4 h-4 text-muted-foreground" />}
                placeholder="Doe"
                error={errors.lastName?.message}
                {...register('lastName')}
              />
            </div>

            {/* Password */}
            <div className="space-y-2">
              <Input
                label="Password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                leftIcon={<Lock className="w-4 h-4 text-muted-foreground" />}
                rightIcon={
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-muted-foreground hover:text-foreground focus:outline-none"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                }
                placeholder="Create a strong password"
                error={errors.password?.message}
                {...register('password')}
              />
              {passwordValue && (() => {
                const strength = getPasswordStrength(passwordValue);
                const labels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong'];
                const colors = ['bg-destructive', 'bg-alert', 'bg-warn', 'bg-info', 'bg-success'];
                return (
                  <div className="mt-2">
                    <div className="flex space-x-1">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <div
                          key={i}
                          className={`h-1 w-full rounded ${i <= strength ? colors[strength - 1] : 'bg-muted'}`}
                        />
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Password strength: {labels[strength - 1] || 'Too weak'}
                    </p>
                  </div>
                );
              })()}
            </div>

            {/* Confirm Password */}
            <Input
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              autoComplete="new-password"
              leftIcon={<Lock className="w-4 h-4 text-muted-foreground" />}
              rightIcon={
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="text-muted-foreground hover:text-foreground focus:outline-none"
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              }
              placeholder="Confirm your password"
              error={errors.confirmPassword?.message}
              {...register('confirmPassword')}
            />

            <Button type="submit" className="w-full" size="lg" isLoading={isSubmitting}>
              Accept Invitation & Create Account
            </Button>

            <div className="text-center">
              <p className="text-sm text-muted-foreground">
                Already have an account?{' '}
                <Link to="/login" className="font-medium text-primary hover:text-primary/80">
                  Sign in
                </Link>
              </p>
            </div>
          </form>
        </Card>
        )}

        <div className="text-center">
          <p className="text-xs text-muted-foreground">
            Your connection is encrypted and your data is isolated to your business.
          </p>
        </div>
      </div>
    </div>
  );
};

export default InviteAcceptPage;
